// AM Fingerprints — admin page to manage pairs of phrases that identify
// answering machines by sequence. When the bot hears phrase_1 then phrase_2
// within `window_seconds`, it hangs up immediately with disposition
// 'answering-machine'. Read by the bot per-call via voice-loader.
//
// New rows take effect on the next bot process restart (or when the in-memory
// bundle cache is invalidated). The "Reload bots" button hits the dashboard
// API which restarts the audiosocket-bot service across the fleet.
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Plus, Trash2, Edit3, Save, X, Power, AlertCircle, Voicemail, RefreshCw, Phone } from 'lucide-react'
import { amFingerprintsApi, audiosocketBotsApi } from '../services/api'

const EMPTY_ROW = { phrase_1: '', phrase_2: '', window_seconds: 3, enabled: 1, notes: '' }

// Business day = 6 PM local → 6 AM local next morning (matches MonitoringV3).
// Used to compute "today" scope so the modal lines up with the stats counter.
const DAY_START_HOUR = 18
const DAY_LENGTH_MS  = 12 * 3600 * 1000
function currentBusinessDayRange() {
  const now = new Date()
  let start
  if (now.getHours() >= DAY_START_HOUR) {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), DAY_START_HOUR, 0, 0, 0)
  } else {
    const y = new Date(now); y.setDate(y.getDate() - 1)
    start = new Date(y.getFullYear(), y.getMonth(), y.getDate(), DAY_START_HOUR, 0, 0, 0)
  }
  return { from: start.toISOString(), to: new Date(start.getTime() + DAY_LENGTH_MS).toISOString() }
}

export default function AmFingerprints() {
  const [rows, setRows] = useState([])
  const [stats, setStats] = useState({})        // { <fp_id>: {total, today, last_caught_at} }
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [editingId, setEditingId] = useState(null)   // id being edited, or 'new'
  const [draft, setDraft] = useState(EMPTY_ROW)
  const [reloading, setReloading] = useState(false)
  const [reloadResult, setReloadResult] = useState(null)
  const [callList, setCallList] = useState(null)     // { fp, scope, loading, calls, error } | null

  const load = async () => {
    setLoading(true)
    setErr(null)
    try {
      const bd = currentBusinessDayRange()
      const [rowsRes, statsRes] = await Promise.all([
        amFingerprintsApi.getAll(),
        amFingerprintsApi.getStats({ today_from: bd.from, today_to: bd.to }).catch(() => ({ data: { stats: {} } }))
      ])
      const data = Array.isArray(rowsRes.data) ? rowsRes.data : []
      data.sort((a, b) => (a.id || 0) - (b.id || 0))
      setRows(data)
      setStats(statsRes.data?.stats || {})
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const startEdit = (row) => {
    setEditingId(row.id)
    setDraft({ ...row, enabled: row.enabled ? 1 : 0 })
  }
  const startNew = () => {
    setEditingId('new')
    setDraft({ ...EMPTY_ROW })
  }
  const cancelEdit = () => {
    setEditingId(null)
    setDraft(EMPTY_ROW)
  }
  const save = async () => {
    if (!draft.phrase_1.trim() || !draft.phrase_2.trim()) {
      alert('Both phrase_1 and phrase_2 are required.')
      return
    }
    const window_seconds = Math.max(1, parseInt(draft.window_seconds) || 3)
    const payload = {
      phrase_1: draft.phrase_1.trim().toLowerCase(),
      phrase_2: draft.phrase_2.trim().toLowerCase(),
      window_seconds,
      enabled: draft.enabled ? 1 : 0,
      notes: (draft.notes || '').trim() || null
    }
    try {
      if (editingId === 'new') {
        await amFingerprintsApi.create(payload)
      } else {
        await amFingerprintsApi.update(editingId, payload)
      }
      cancelEdit()
      await load()
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message))
    }
  }
  const toggle = async (row) => {
    try {
      await amFingerprintsApi.update(row.id, { enabled: row.enabled ? 0 : 1 })
      await load()
    } catch (e) {
      alert('Toggle failed: ' + (e.response?.data?.error || e.message))
    }
  }
  const remove = async (row) => {
    if (!confirm(`Delete fingerprint "${row.phrase_1}" → "${row.phrase_2}"?`)) return
    try {
      await amFingerprintsApi.delete(row.id)
      await load()
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.error || e.message))
    }
  }

  const openCallList = async (fp, scope /* 'total' | 'today' */) => {
    setCallList({ fp, scope, loading: true, calls: [], error: null })
    try {
      const params = {
        disposition: 'answering-machine',
        search: `AM fingerprint #${fp.id}:`,
        per_page: 200
      }
      if (scope === 'today') {
        const r = currentBusinessDayRange()
        params.from = r.from
        params.to = r.to
      }
      const res = await audiosocketBotsApi.listCallLogs(params)
      setCallList({ fp, scope, loading: false, calls: res.data?.calls || [], error: null })
    } catch (e) {
      setCallList(cl => cl && { ...cl, loading: false, error: e.response?.data?.error || e.message })
    }
  }

  const reloadFleet = async (force = false) => {
    if (force && !confirm('Force-restart ALL bots — this will DROP any active calls in progress. Continue?')) return
    setReloading(true)
    setReloadResult(null)
    try {
      const res = await audiosocketBotsApi.reloadFleet({ force })
      setReloadResult(res.data)
    } catch (e) {
      setReloadResult({ success: false, error: e.response?.data?.error || e.message })
    } finally {
      setReloading(false)
    }
  }

  return (
    <div style={{ padding: '20px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Voicemail size={22} /> Answering-Machine Fingerprints
          </h2>
          <p style={{ margin: '6px 0 0 0', fontSize: 13, color: 'var(--text-muted)', maxWidth: 800 }}>
            When the bot hears <b>phrase 1</b> followed by <b>phrase 2</b> within the time window, it hangs up immediately with disposition <code>answering-machine</code>.
            Phrases are matched with word-boundary regex (so <code>"uh"</code> catches <i>"Uh..."</i>, <i>"Uhhhh"</i>, but not <i>"uhuh"</i>).
            <br />
            <span style={{ color: 'var(--warning)' }}>Note:</span> changes take effect on the next bot restart (cache is per-process).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary"
            onClick={() => reloadFleet(false)}
            disabled={reloading}
            title="Restart idle bots so they pick up the new fingerprints (busy bots are skipped)"
          >
            <RefreshCw size={14} className={reloading ? 'spinning' : ''} /> {reloading ? 'Reloading…' : 'Reload bots'}
          </button>
          <button className="btn btn-primary" onClick={startNew} disabled={editingId === 'new'}>
            <Plus size={16} /> New fingerprint
          </button>
        </div>
      </div>

      {reloadResult && (
        <div style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: reloadResult.success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${reloadResult.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>
              {reloadResult.success
                ? `Reload complete: ${reloadResult.ok_count}/${reloadResult.count} restarted${reloadResult.skipped_count ? `, ${reloadResult.skipped_count} skipped (on a call)` : ''}`
                : `Reload failed: ${reloadResult.error}`}
            </strong>
            <button onClick={() => setReloadResult(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={14} /></button>
          </div>
          {reloadResult.success && reloadResult.results && (
            <div style={{ fontSize: 12, fontFamily: 'monospace', maxHeight: 160, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4 }}>
              {reloadResult.results.map(r => (
                <div key={r.machine_id} style={{ color: r.ok ? '#22c55e' : (r.skipped ? '#f59e0b' : '#ef4444') }}>
                  SIP {r.sip_user} (LXC {r.lxc_id}): {r.status}
                </div>
              ))}
            </div>
          )}
          {reloadResult.success && reloadResult.skipped_count > 0 && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <button className="btn btn-sm btn-danger" onClick={() => reloadFleet(true)}>
                Force-restart skipped ({reloadResult.skipped_count}) — drops their active calls
              </button>
            </div>
          )}
        </div>
      )}

      {err && (
        <div style={{ padding: 12, background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={16} /> {err}
        </div>
      )}

      <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.03)', textAlign: 'left' }}>
              <th style={{ padding: '10px 14px', width: 50 }}>#</th>
              <th style={{ padding: '10px 14px' }}>Phrase 1</th>
              <th style={{ padding: '10px 14px' }}>Phrase 2</th>
              <th style={{ padding: '10px 14px', width: 110 }}>Window (s)</th>
              <th style={{ padding: '10px 14px', width: 150 }} title="Calls hung up as answering-machine by this fingerprint">Caught</th>
              <th style={{ padding: '10px 14px' }}>Notes</th>
              <th style={{ padding: '10px 14px', width: 90 }}>Enabled</th>
              <th style={{ padding: '10px 14px', width: 140, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {editingId === 'new' && (
              <EditRow draft={draft} setDraft={setDraft} onSave={save} onCancel={cancelEdit} idLabel="new" />
            )}
            {loading && rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
            ) : rows.length === 0 && editingId !== 'new' ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No fingerprints yet. Click "New fingerprint" to add one.</td></tr>
            ) : rows.map(row => (
              editingId === row.id
                ? <EditRow key={row.id} draft={draft} setDraft={setDraft} onSave={save} onCancel={cancelEdit} idLabel={String(row.id)} />
                : <ViewRow key={row.id} row={row} stat={stats[row.id]} onEdit={() => startEdit(row)} onToggle={() => toggle(row)} onDelete={() => remove(row)} onOpenList={(scope) => openCallList(row, scope)} />
            ))}
          </tbody>
        </table>
      </div>

      {callList && (
        <CallListModal
          fp={callList.fp}
          scope={callList.scope}
          loading={callList.loading}
          calls={callList.calls}
          error={callList.error}
          onClose={() => setCallList(null)}
        />
      )}
    </div>
  )
}

function ViewRow({ row, stat, onEdit, onToggle, onDelete, onOpenList }) {
  const total = stat?.total ?? 0
  const today = stat?.today ?? 0
  const lastAt = stat?.last_caught_at
  const lastTip = lastAt ? `Last caught: ${new Date(lastAt).toLocaleString()}` : 'No catches yet'
  return (
    <motion.tr
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ borderTop: '1px solid var(--border-color)', opacity: row.enabled ? 1 : 0.5 }}
    >
      <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{row.id}</td>
      <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>{row.phrase_1}</td>
      <td style={{ padding: '10px 14px', fontFamily: 'monospace' }}>{row.phrase_2}</td>
      <td style={{ padding: '10px 14px' }}>{row.window_seconds}</td>
      <td style={{ padding: '10px 14px' }} title={lastTip}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => total > 0 && onOpenList('total')}
            disabled={total === 0}
            title={total > 0 ? `View all ${total} calls caught by this fingerprint` : 'No catches yet'}
            style={{
              display: 'inline-block',
              minWidth: 32,
              padding: '2px 8px',
              borderRadius: 10,
              background: total > 0 ? 'rgba(245,158,11,0.15)' : 'rgba(107,114,128,0.12)',
              color: total > 0 ? '#f59e0b' : '#9ca3af',
              fontWeight: 600,
              fontSize: 12,
              textAlign: 'center',
              border: 'none',
              cursor: total > 0 ? 'pointer' : 'default'
            }}
          >{total}</button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            today:{' '}
            <button
              type="button"
              onClick={() => today > 0 && onOpenList('today')}
              disabled={today === 0}
              title={today > 0 ? `View ${today} calls caught today` : 'No catches today'}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: today > 0 ? '#f59e0b' : 'var(--text-muted)',
                fontWeight: 700,
                textDecoration: today > 0 ? 'underline' : 'none',
                cursor: today > 0 ? 'pointer' : 'default',
                fontSize: 11
              }}
            >{today}</button>
          </span>
        </div>
      </td>
      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.notes || '—'}</td>
      <td style={{ padding: '10px 14px' }}>
        <button
          className="btn btn-sm"
          onClick={onToggle}
          style={{
            background: row.enabled ? 'rgba(34,197,94,0.15)' : 'rgba(107,114,128,0.15)',
            color: row.enabled ? '#22c55e' : '#9ca3af',
            border: 'none',
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            cursor: 'pointer'
          }}
        >
          <Power size={11} style={{ marginRight: 4 }} />
          {row.enabled ? 'ON' : 'OFF'}
        </button>
      </td>
      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
        <button className="btn btn-sm btn-secondary" onClick={onEdit} style={{ marginRight: 6 }}>
          <Edit3 size={12} /> Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={onDelete}>
          <Trash2 size={12} />
        </button>
      </td>
    </motion.tr>
  )
}

function EditRow({ draft, setDraft, onSave, onCancel, idLabel }) {
  return (
    <tr style={{ borderTop: '1px solid var(--border-color)', background: 'rgba(59,130,246,0.05)' }}>
      <td style={{ padding: '8px 14px', color: 'var(--text-muted)' }}>{idLabel}</td>
      <td style={{ padding: '8px 14px' }}>
        <input className="input" style={{ width: '100%' }} value={draft.phrase_1} onChange={e => setDraft({ ...draft, phrase_1: e.target.value })} placeholder="e.g. uh" autoFocus />
      </td>
      <td style={{ padding: '8px 14px' }}>
        <input className="input" style={{ width: '100%' }} value={draft.phrase_2} onChange={e => setDraft({ ...draft, phrase_2: e.target.value })} placeholder="e.g. can you hear me" />
      </td>
      <td style={{ padding: '8px 14px' }}>
        <input type="number" min="1" max="30" className="input" style={{ width: 80 }} value={draft.window_seconds} onChange={e => setDraft({ ...draft, window_seconds: parseInt(e.target.value) || 3 })} />
      </td>
      <td style={{ padding: '8px 14px', color: 'var(--text-muted)', fontSize: 11 }}>—</td>
      <td style={{ padding: '8px 14px' }}>
        <input className="input" style={{ width: '100%' }} value={draft.notes || ''} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="optional" />
      </td>
      <td style={{ padding: '8px 14px' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked ? 1 : 0 })} />
          {draft.enabled ? 'ON' : 'OFF'}
        </label>
      </td>
      <td style={{ padding: '8px 14px', textAlign: 'right' }}>
        <button className="btn btn-sm btn-primary" onClick={onSave} style={{ marginRight: 6 }}>
          <Save size={12} /> Save
        </button>
        <button className="btn btn-sm btn-secondary" onClick={onCancel}>
          <X size={12} />
        </button>
      </td>
    </tr>
  )
}

function fmtTime(s) {
  if (!s) return '—'
  // Handles both ISO ("...T01:07:21.000Z") and MySQL space-format ("2026-05-22 01:07:21").
  const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(s)
  const d = new Date(hasTz ? s : s.replace(' ', 'T') + 'Z')
  if (isNaN(d.getTime())) return s
  return d.toLocaleString()
}

function fmtDur(s) {
  if (!s && s !== 0) return '—'
  const n = Math.round(Number(s) || 0)
  const m = Math.floor(n / 60)
  const r = n % 60
  return m > 0 ? `${m}m ${r}s` : `${r}s`
}

function CallListModal({ fp, scope, loading, calls, error, onClose }) {
  const scopeLabel = scope === 'today' ? 'today' : 'all time'
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(960px, 95vw)', maxHeight: '85vh', overflow: 'auto',
          background: 'var(--bg-card, #1a1a1a)', border: '1px solid var(--border-color)',
          borderRadius: 12, padding: 18, boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Phone size={18} /> Calls caught by fingerprint #{fp.id}
            </h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'monospace' }}>
              "{fp.phrase_1}" → "{fp.phrase_2}"  ·  window {fp.window_seconds}s  ·  {scopeLabel} ({calls.length})
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 8, marginBottom: 10 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : calls.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No matching calls.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: 'rgba(255,255,255,0.03)' }}>
                <th style={{ padding: '8px 10px' }}>Time</th>
                <th style={{ padding: '8px 10px' }}>Phone</th>
                <th style={{ padding: '8px 10px', width: 80 }}>SIP</th>
                <th style={{ padding: '8px 10px', width: 90 }}>Duration</th>
                <th style={{ padding: '8px 10px' }}>Recording</th>
              </tr>
            </thead>
            <tbody>
              {calls.map(c => {
                const url = c.machine_id && c.recording_filename
                  ? audiosocketBotsApi.machineRecordingUrl(c.machine_id, c.recording_filename)
                  : null
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                      {fmtTime(c.started_at)}
                    </td>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{c.phone_number || '—'}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{c.sip_user || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{fmtDur(c.duration_seconds)}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {url
                        ? <audio controls preload="none" src={url} style={{ height: 28, maxWidth: 260 }} />
                        : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
